import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, ShoppingCart } from 'lucide-react';
import { ProgramPurchaseModal } from './ProgramPurchaseModal';
import { toast } from 'sonner';

interface MentorProgram {
  id: string;
  title: string;
  description: string | null;
  session_count: number;
  total_tokens_required: number;
  discount_percentage: number;
}

interface MentorProgramsDisplayProps {
  mentorId: string;
  mentorName: string;
}

export const MentorProgramsDisplay = ({ mentorId, mentorName }: MentorProgramsDisplayProps) => {
  const [programs, setPrograms] = useState<MentorProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentBalance, setStudentBalance] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState<MentorProgram | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    fetchPrograms();
    checkAuthAndBalance();
  }, [mentorId]);

  const checkAuthAndBalance = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setIsAuthenticated(true);
      
      // Get student's token balance
      const { data: credits } = await supabase
        .from('student_credits')
        .select('balance')
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      setStudentBalance(credits?.balance || 0);
    }
  };

  const fetchPrograms = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_programs')
        .select('*')
        .eq('mentor_id', mentorId)
        .eq('is_active', true)
        .order('total_tokens_required', { ascending: true });

      if (error) throw error;
      setPrograms(data || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!programs.length) {
    return null;
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Session Programs</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {programs.map((program) => (
            <Card key={program.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base">{program.title}</CardTitle>
                    {program.description && (
                      <CardDescription className="mt-1 whitespace-pre-line">{program.description}</CardDescription>
                    )}
                  </div>
                  <Package className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-3 text-sm">
                  <Badge variant="outline">
                    {program.session_count} Sessions
                  </Badge>
                  <Badge variant="outline">
                    {program.total_tokens_required} Tokens
                  </Badge>
                  {program.discount_percentage > 0 && (
                    <Badge variant="default" className="bg-green-600">
                      Save {program.discount_percentage}%
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  ≈{(program.total_tokens_required / program.session_count).toFixed(0)} tokens per session
                </div>
                {isAuthenticated && (
                  <Button
                    className="w-full mt-2"
                    size="sm"
                    onClick={() => setSelectedProgram(program)}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Purchase Program
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {selectedProgram && (
        <ProgramPurchaseModal
          isOpen={!!selectedProgram}
          onClose={() => setSelectedProgram(null)}
          program={selectedProgram}
          mentorId={mentorId}
          mentorName={mentorName}
          studentBalance={studentBalance}
          onPurchaseSuccess={() => {
            checkAuthAndBalance();
            toast.success('You can now use your program sessions when booking!');
          }}
        />
      )}
    </>
  );
};
